import React, { useState, useEffect } from "react";
import { PaiementService } from "../../services/paiements/paiementService";
import { CreditCard, Download, Search, Filter, CheckCircle, Clock, XCircle, FileText } from "lucide-react";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { exportToCSV } from "../../utils/exportUtils";

interface Paiement {
  id: string;
  reference: string;
  montant: number;
  date_creation: string;
  date_execution?: string;
  statut: "initie" | "valide" | "execute" | "rejete" | "echoue";
  facture?: {
    id: string;
    numero: string;
    fournisseur_nom?: string;
  };
  beneficiaire_nom?: string;
}

export const ClientPaiements: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [paiements, setPaiements] = useState<Paiement[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchPaiements = async () => {
    try {
      setLoading(true);
      const response = await PaiementService.getPaiements();
      setPaiements(response.data.results || []);
    } catch (error) {
      console.error("Erreur chargement paiements", error);
      toast.error(t('paiements.messages.errorLoad')); // Reuse or common
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaiements();
  }, []);

  const filteredPaiements = paiements.filter((p) => {
    const matchesSearch = 
      p.reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.beneficiaire_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.facture?.numero.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || p.statut === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusConfig = (statut: string) => {
    switch (statut) {
      case "execute":
        return { color: "bg-success-100 text-success-800", icon: CheckCircle, label: t('paiements.statuses.execute') };
      case "valide":
        return { color: "bg-primary-100 text-primary-800", icon: CheckCircle, label: t('paiements.statuses.valide') || "Validé" };
      case "initie":
        return { color: "bg-warning-100 text-warning-800", icon: Clock, label: t('paiements.statuses.initie') };
      case "rejete":
        return { color: "bg-error-100 text-error-800", icon: XCircle, label: t('paiements.statuses.rejete') || "Rejeté" };
      case "echoue":
        return { color: "bg-error-100 text-error-800", icon: XCircle, label: t('paiements.statuses.echec') };
      default:
        return { color: "bg-gray-100 text-gray-800", icon: Clock, label: statut };
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(i18n.language.startsWith('fr') ? 'fr-FR' : 'en-US', {
      style: "currency",
      currency: "XOF",
    }).format(amount);
  };

  if (loading && paiements.length === 0) {
      return (
          <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
      );
  }

  // Stats
  const totalExecute = paiements.filter(p => p.statut === 'execute').reduce((acc, curr) => acc + curr.montant, 0);
  const totalEnAttente = paiements.filter(p => ['initie', 'valide'].includes(p.statut)).reduce((acc, curr) => acc + curr.montant, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('paiements.clientPages.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {t('paiements.clientPages.overview')}
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button 
            onClick={() => exportToCSV(paiements, "client_paiements")}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
            <Download className="h-4 w-4 mr-2" />
            {t('common.download')}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3 rounded-lg bg-success-100">
                  <CreditCard className="h-6 w-6 text-success-600" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('paiements.clientPages.totalExecuted')}
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900">
                    {formatCurrency(totalExecute)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="p-3 rounded-lg bg-warning-100">
                  <Clock className="h-6 w-6 text-warning-600" />
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    {t('paiements.clientPages.pending')}
                  </dt>
                  <dd className="text-2xl font-semibold text-gray-900">
                    {formatCurrency(totalEnAttente)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <div className="relative rounded-md shadow-sm max-w-xs w-full">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        className="focus:ring-primary-500 focus:border-primary-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                        placeholder={t('header.search')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="flex items-center space-x-3">
                    <Filter className="h-5 w-5 text-gray-400" />
                    <select
                        className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">{t('paiements.clientPages.allStatuses')}</option>
                        <option value="initie">{t('paiements.statuses.initie')}</option>
                        <option value="valide">{t('paiements.statuses.valide') || "Validé"}</option>
                        <option value="execute">{t('paiements.statuses.execute')}</option>
                        <option value="rejete">{t('paiements.statuses.rejete') || "Rejeté"}</option>
                    </select>
                </div>
            </div>
        </div>

        {/* List */}
        <div className="divide-y divide-gray-200">
            {filteredPaiements.length === 0 ? (
                 <div className="p-8 text-center text-gray-500">
                    {t('paiements.noPaymentFound')}
                </div>
            ) : (
                filteredPaiements.map((paiement) => {
                    const status = getStatusConfig(paiement.statut);
                    const StatusIcon = status.icon;
                    return (
                        <div key={paiement.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                     <div className="flex-shrink-0">
                                        <div className="p-2 bg-gray-100 rounded-lg">
                                            <FileText className="h-6 w-6 text-gray-600" />
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex items-center">
                                            <span className="text-sm font-medium text-gray-900">
                                                {paiement.reference}
                                            </span>
                                            <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                                                <StatusIcon className="h-3 w-3 mr-1" />
                                                {status.label}
                                            </span>
                                        </div>
                                        <div className="mt-1 text-sm text-gray-500">
                                            {paiement.beneficiaire_nom || paiement.facture?.fournisseur_nom || t('common.unknown')}
                                        </div>
                                        <div className="text-xs text-gray-400">
                                            {t('paiements.clientPages.createdOn')} {format(new Date(paiement.date_creation), "dd MMMM yyyy", { locale: i18n.language.startsWith('fr') ? fr : enUS })}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm font-bold text-gray-900">
                                        {formatCurrency(paiement.montant)}
                                    </div>
                                    {paiement.facture && (
                                        <div className="text-xs text-gray-500">
                                            Ref: {paiement.facture.numero}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })
            )}
        </div>
      </div>
    </div>
  );
};
